import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ContractsPage from './pages/contracts/ContractsPage';
import StaffPage from './pages/staff/StaffPage';
import ProfilePage from './pages/profile/ProfilePage';
import GasReadingsPage from './pages/gas-readings/GasReadingsPage';
import UsersPage from './pages/users/UsersPage';
import QuotationsPage from './pages/quotations/QuotationsPage';
import ServicesPage from './pages/services/ServicesPage';
import ServiceDetailPage from './pages/services/ServiceDetailPage';
import BookingsPage from './pages/bookings/BookingsPage';
import VisitorsPage from './pages/visitors/VisitorsPage';
import ApartmentMembersPage from './pages/members/ApartmentMembersPage';
import CalendarPage from './pages/calendar/CalendarPage';
import PaymentTrackerPage from './pages/payments/PaymentTrackerPage';
import HousekeepingPage from './pages/housekeeping/HousekeepingPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import FacilityCompliancePage from './pages/facility-compliance/FacilityCompliancePage';

// Ordered list used to pick the first allowed page for custom-role users
const FEATURE_ROUTES = [
  { feature: 'dashboard',    path: '/dashboard' },
  { feature: 'payments',     path: '/payments' },
  { feature: 'contracts',    path: '/contracts' },
  { feature: 'quotations',   path: '/quotations' },
  { feature: 'services',     path: '/services' },
  { feature: 'bookings',     path: '/bookings' },
  { feature: 'visitors',     path: '/visitors' },
  { feature: 'members',      path: '/members' },
  { feature: 'security',     path: '/security' },
  { feature: 'cleaning',     path: '/cleaning' },
  { feature: 'housekeeping', path: '/housekeeping' },
  { feature: 'water_treatment', path: '/water-treatment' },
  { feature: 'gas_readings',        path: '/gas-readings' },
  { feature: 'facility_management', path: '/facility-compliance' },
  { feature: 'calendar',            path: '/calendar' },
  { feature: 'notifications',       path: '/notifications' },
  { feature: 'staff',               path: '/staff' },
];

function getFirstAllowedRoute(user) {
  const features = user?.allowed_features || [];
  for (const { feature, path } of FEATURE_ROUTES) {
    if (features.includes(feature)) return path;
  }
  return '/profile';
}

const Loading = () => (
  <div className="min-h-screen bg-page flex items-center justify-center">
    <div className="text-gray-500">Loading...</div>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Standard admin route — blocks gas_reader; for custom role, redirects if no allowed features at all
function AdminRoute({ children, feature }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'gas_reader') return <Navigate to="/gas-readings" replace />;

  if (user.role === 'caretaker' && !user.allowed_features?.length) {
    return <Navigate to="/gas-readings" replace />;
  }

  // Custom role: check if this specific feature is allowed
  if (user.role === 'custom' && feature) {
    if (!user.allowed_features?.includes(feature)) {
      return <Navigate to={getFirstAllowedRoute(user)} replace />;
    }
  }

  return children;
}

function FacilityRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'gas_reader' || user.role === 'security_guard') {
    return <Navigate to="/gas-readings" replace />;
  }
  if (user.role === 'custom' && !user.allowed_features?.includes('facility_management')) {
    return <Navigate to={getFirstAllowedRoute(user)} replace />;
  }
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'superadmin' && user.role !== 'admin') {
    return user.role === 'gas_reader'
      ? <Navigate to="/gas-readings" replace />
      : <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) {
    if (user.role === 'gas_reader') return <Navigate to="/gas-readings" replace />;
    if (user.role === 'custom') return <Navigate to={getFirstAllowedRoute(user)} replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'gas_reader') return <Navigate to="/gas-readings" replace />;
  if (user.role === 'custom') return <Navigate to={getFirstAllowedRoute(user)} replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      <Route path="/dashboard"   element={<AdminRoute feature="dashboard"><DashboardPage /></AdminRoute>} />
      <Route path="/calendar"    element={<AdminRoute feature="calendar"><CalendarPage /></AdminRoute>} />
      <Route path="/notifications" element={<AdminRoute feature="notifications"><NotificationsPage /></AdminRoute>} />
      <Route path="/contracts"   element={<AdminRoute feature="contracts"><ContractsPage /></AdminRoute>} />
      <Route path="/quotations"  element={<AdminRoute feature="quotations"><QuotationsPage /></AdminRoute>} />
      <Route path="/staff"       element={<AdminRoute feature="staff"><StaffPage /></AdminRoute>} />
      <Route path="/services"    element={<AdminRoute feature="services"><ServicesPage /></AdminRoute>} />
      <Route path="/security"    element={<AdminRoute feature="security"><ServiceDetailPage /></AdminRoute>} />
      <Route path="/cleaning"    element={<AdminRoute feature="cleaning"><ServiceDetailPage /></AdminRoute>} />
      <Route path="/water-treatment" element={<AdminRoute feature="water_treatment"><ServiceDetailPage /></AdminRoute>} />
      <Route path="/garden"      element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/pool"        element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/housekeeping" element={<AdminRoute feature="housekeeping"><HousekeepingPage /></AdminRoute>} />
      <Route path="/payments"    element={<AdminRoute feature="payments"><PaymentTrackerPage /></AdminRoute>} />
      <Route path="/visitors"    element={<AdminRoute feature="visitors"><VisitorsPage /></AdminRoute>} />
      <Route path="/members"     element={<AdminRoute feature="members"><ApartmentMembersPage /></AdminRoute>} />
      <Route path="/bookings"    element={<AdminRoute feature="bookings"><BookingsPage /></AdminRoute>} />

      <Route path="/facility-compliance" element={<FacilityRoute><FacilityCompliancePage /></FacilityRoute>} />
      <Route path="/profile"     element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/users"       element={<SuperAdminRoute><UsersPage /></SuperAdminRoute>} />
      <Route path="/gas-readings" element={<ProtectedRoute><GasReadingsPage /></ProtectedRoute>} />
      <Route path="/"  element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
      <Route path="*"  element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
